import { UserAvatar } from "@/components/UserAvatar";
import { MarkdownRenderer } from "@/components/ui/MarkdownRenderer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useReports } from "@/hooks/useReports";
import type { Report } from "@/types";
import { format } from "date-fns";
import { AlertTriangle, CalendarIcon, Check, CheckCircle2, ChevronsUpDown, Clock, FileText, Filter, Target, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";
import { ModernPagination } from "./ModernPagination";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { userService } from "@/services/userService";
import { cn } from "@/lib/utils";

export const ReportsManagement = () => {
    const {
        reports,
        total,
        loading,
        page,
        setPage,
        limit,
        filters,
        setFilters,
    } = useReports(1, 10);
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [allUsers, setAllUsers] = useState<{ label: string, value: string }[]>([]);
    const [date, setDate] = useState<Date | undefined>(undefined);
    const [openUserFilter, setOpenUserFilter] = useState(false);

    // Fetch users for the select filter
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await userService.getCompanyUsers("", 1, 100);
                const options = response.data.map(user => ({
                    label: user.fullName,
                    value: user.pid
                }));
                setAllUsers(options);
            } catch (error) {
                console.error("Failed to fetch users:", error);
            }
        };
        fetchUsers();
    }, []);

    // Sync date state with filters
    useEffect(() => {
        if (date) {
            setFilters(prev => ({
                ...prev,
                reportDate: new Date(date)
            }));
        } else if (!date && filters.reportDate) {
            setFilters(prev => ({
                ...prev,
                reportDate: undefined
            }));
        }
    }, [date, filters.reportDate]);

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6">
            <Card className="overflow-hidden shadow-lg">
                <CardHeader className="pb-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <CardTitle className="text-xl">Staff Reports</CardTitle>
                            <CardDescription>Monitor daily progress and address blockers</CardDescription>
                        </div>
                        <div className="grid grid-cols-1 gap-3 w-full sm:flex sm:flex-wrap sm:items-center sm:justify-end md:w-auto">
                            {/* User Filter (Searchable) */}
                            <Popover open={openUserFilter} onOpenChange={setOpenUserFilter}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openUserFilter}
                                        className="w-full sm:w-[200px] justify-between h-10 border-muted-foreground/20 font-normal bg-background"
                                    >
                                        <div className="flex items-center gap-2 truncate">
                                            <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                                            <span className="truncate">
                                                {filters.userPid
                                                    ? allUsers.find((user) => user.value === filters.userPid)?.label
                                                    : "All Users"}
                                            </span>
                                        </div>
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[200px] p-0" align="start">
                                    <Command>
                                        <CommandInput placeholder="Search user..." />
                                        <CommandList>
                                            <CommandEmpty>No user found.</CommandEmpty>
                                            <CommandGroup>
                                                <CommandItem
                                                    value="all"
                                                    onSelect={() => {
                                                        setFilters(prev => ({ ...prev, userPid: undefined }));
                                                        setOpenUserFilter(false);
                                                    }}
                                                >
                                                    <Check
                                                        className={cn(
                                                            "mr-2 h-4 w-4",
                                                            !filters.userPid ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                    All Users
                                                </CommandItem>
                                                {allUsers.map((user) => (
                                                    <CommandItem
                                                        key={user.value}
                                                        value={user.label}
                                                        onSelect={() => {
                                                            setFilters(prev => ({ ...prev, userPid: user.value }));
                                                            setOpenUserFilter(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                filters.userPid === user.value ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        {user.label}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>

                            {/* Date Filter */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant={"outline"}
                                        className={cn(
                                            "w-full sm:w-[180px] justify-start text-left font-normal h-10 border-muted-foreground/20 bg-background",
                                            !date && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                                        <span className="truncate">
                                            {date ? format(date, "LLL dd, y") : "Pick a date"}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="end">
                                    <Calendar
                                        initialFocus
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                    />
                                    <div className="p-3 border-t bg-muted/50 flex justify-end">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setDate(undefined)}
                                            className="text-xs h-8"
                                        >
                                            Clear Date
                                        </Button>
                                    </div>
                                </PopoverContent>
                            </Popover>

                            {/* Status Filter */}
                            <Select
                                value={filters.blocker === true ? "blocked" : filters.blocker === false ? "on-track" : "all"}
                                onValueChange={(value) => {
                                    setFilters(prev => ({
                                        ...prev,
                                        blocker: value === "blocked" ? true : value === "on-track" ? false : undefined
                                    }));
                                }}
                            >
                                <SelectTrigger className="w-full sm:w-[150px] h-10 bg-background border-muted-foreground/20">
                                    <div className="flex items-center gap-2">
                                        <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
                                        <SelectValue placeholder="All Status" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="blocked">Blocked only</SelectItem>
                                    <SelectItem value="on-track">Not blocked</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[180px]">User</TableHead>
                                    <TableHead className="hidden sm:table-cell w-[140px]">Date</TableHead>
                                    <TableHead className="hidden lg:table-cell">Preview</TableHead>
                                    <TableHead className="hidden md:table-cell min-w-[150px]">Blockers</TableHead>
                                    <TableHead className="text-right sm:text-left w-[100px] sm:w-[150px]">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <>
                                        {[1, 2, 3, 4].map((i) => (
                                            <TableRow key={i}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <Skeleton className="h-9 w-9 rounded-full" />
                                                        <Skeleton className="h-4 w-28" />
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden sm:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                                                <TableCell className="hidden lg:table-cell"><Skeleton className="h-4 w-48" /></TableCell>
                                                <TableCell className="hidden md:table-cell"><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                                                <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                                            </TableRow>
                                        ))}
                                    </>
                                ) : reports.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                                            No reports found matching your criteria.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    reports.map((report) => (
                                        <TableRow
                                            key={report.pid}
                                            className="group hover:bg-muted/30 transition-colors cursor-pointer"
                                            onClick={() => setSelectedReport(report)}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <UserAvatar
                                                        src={report.user?.avatarUrl}
                                                        name={report.user?.fullName}
                                                        className="h-9 w-9 border border-muted"
                                                    />
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="font-semibold truncate">{report.user.fullName}</span>
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {report.priority && (
                                                                <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 uppercase font-bold bg-secondary/50">
                                                                    {report.priority.code}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="hidden sm:table-cell text-sm font-medium text-muted-foreground whitespace-nowrap">
                                                {format(new Date(report.reportDate), "MMM dd, yyyy")}
                                            </TableCell>
                                            <TableCell className="hidden lg:table-cell">
                                                <p className="text-sm text-muted-foreground line-clamp-1 max-w-[550px]">
                                                    {report.content}
                                                </p>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                {report.blocker && report.blockedReason ? (
                                                    <p className="text-xs text-destructive font-medium line-clamp-2 max-w-[200px] italic">
                                                        "{report.blockedReason}"
                                                    </p>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground/50">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right sm:text-left">
                                                <div className="flex items-center justify-end sm:justify-start gap-2">
                                                    {report.blocker ? (
                                                        <Badge variant="destructive" className="gap-1.5 px-2 py-1 shadow-sm">
                                                            <AlertTriangle className="h-3.5 w-3.5" />
                                                            <span className="hidden xs:inline">Blocker</span>
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="gap-1.5 px-2 py-1 bg-success/5 text-success border-success/20 shadow-sm">
                                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                                            <span className="hidden xs:inline">On Track</span>
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="py-6 border-t bg-muted/10">
                        <ModernPagination
                            page={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            total={total}
                            limit={limit}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Report Detail Modal - Professional Design */}
            <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
                <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-xl shadow-2xl">
                    {/* Enhanced Header with Gradient */}
                    <DialogHeader className="p-0 pb-0 text-left">
                        <div className="bg-gradient-to-br from-primary/5 via-primary/3 to-transparent p-4 sm:p-6 md:p-8 border-b">
                            <div className="flex items-start gap-3 sm:gap-4 md:gap-5">
                                <UserAvatar
                                    src={selectedReport?.user?.avatarUrl}
                                    name={selectedReport?.user?.fullName}
                                    className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 border-2 border-primary/20 shadow-lg ring-4 ring-primary/5"
                                />
                                <div className="flex-1 min-w-0 text-left">
                                    <DialogTitle className="text-lg sm:text-xl md:text-2xl font-bold mb-1.5 sm:mb-2 text-left">
                                        {selectedReport?.user?.fullName}
                                    </DialogTitle>
                                    <DialogDescription className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm md:text-base">
                                        <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                                        <span className="font-medium">
                                            {selectedReport && (
                                                <>
                                                    <span className="hidden sm:inline">{format(new Date(selectedReport.reportDate), "EEEE, MMMM dd, yyyy")}</span>
                                                    <span className="sm:hidden">{format(new Date(selectedReport.reportDate), "MMM dd, yyyy")}</span>
                                                </>
                                            )}
                                        </span>
                                    </DialogDescription>

                                    {/* Status Badge */}
                                    <div className="mt-2 sm:mt-3">
                                        {selectedReport?.blocker ? (
                                            <Badge variant="destructive" className="gap-1.5 px-3 py-1.5 text-xs font-semibold shadow-sm">
                                                <AlertTriangle className="h-3.5 w-3.5" />
                                                Blocked
                                            </Badge>
                                        ) : (
                                            <Badge className="gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20">
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                On Track
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DialogHeader>

                    {/* Enhanced Content Area */}
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6">
                        <div className="space-y-4 sm:space-y-5 md:space-y-6">
                                {/* Priority Information - Professional Card */}
                                {selectedReport?.priority && (
                                    <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
                                        <div className="flex items-center gap-1.5 sm:gap-2">
                                            <Target className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                                            <h4 className="text-xs sm:text-sm font-bold text-foreground uppercase tracking-wide">
                                                Related Priority
                                            </h4>
                                        </div>
                                        <div className="rounded-lg sm:rounded-xl p-3 sm:p-4 border">
                                            <div className="flex items-center gap-2.5 sm:gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm sm:text-base font-semibold text-foreground break-words">
                                                        {selectedReport.priority.code}: {selectedReport.priority.name}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Blocker Alert - Enhanced */}
                                {selectedReport?.blocker && selectedReport.blockedReason && (
                                    <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
                                        <div className="flex items-center gap-1.5 sm:gap-2">
                                            <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-destructive flex-shrink-0" />
                                            <h4 className="text-xs sm:text-sm font-bold text-destructive uppercase tracking-wide">
                                                Blocker Details
                                            </h4>
                                        </div>
                                        <div className="rounded-lg sm:rounded-xl border-2 border-destructive/20 bg-gradient-to-br from-destructive/10 to-destructive/5 p-3 sm:p-4 md:p-5 shadow-sm">
                                            <div className="flex gap-2 sm:gap-3">
                                                <div className="flex-shrink-0 w-0.5 sm:w-1 bg-destructive rounded-full"></div>
                                                <p className="text-xs sm:text-sm text-destructive/90 leading-relaxed italic font-medium break-words">
                                                    "{selectedReport.blockedReason}"
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Report Content - Professional Section */}
                                <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                        <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                                        <h4 className="text-xs sm:text-sm font-bold text-foreground uppercase tracking-wide">
                                            Report Details
                                        </h4>
                                    </div>
                                    <div className="rounded-lg sm:rounded-xl border p-3.5 sm:p-5 md:p-6 ">
                                        <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-bold prose-p:leading-relaxed prose-p:text-foreground/90">
                                            <MarkdownRenderer content={selectedReport?.content || ""} />
                                        </div>
                                    </div>
                                </div>
                        </div>
                    </div>

                    {/* Enhanced Footer */}
                    <div className="border-t bg-muted/30 px-4 sm:px-6 md:px-8 py-3 sm:py-4 flex items-center justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setSelectedReport(null)}
                            className="w-full sm:w-auto font-semibold px-4 sm:px-6"
                            size="default"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
